import React from 'react';
import { Box, Typography, Avatar, Button, Link as MuiLink, useTheme } from '@mui/material';
import { lighten } from '@mui/system';
import getIconForType from '../../utils/icons/getIconForType';
import placeholderImage from '../../images/placeholder-grey-image.jpg';
import avatarPlaceholder from '../../images/avatar-cropped.png';
import ProductsServicesPreview from '../links/productsServices/ProductsServicesPreview/ProductsServicesPreview';
import { useEventTracking } from '../../services/eventTrackingService';
import { useContactLinkContext } from 'contexts/ContactLinkContext';

const DefaultCardDesign = ({
  cardId,
  cardFullName,
  cardJobTitle,
  cardCompany,
  cardTheme,
  cardProfilePictureUrl,
  cardCoverPhotoUrl,
  cardCompanyLogoUrl,
  contextCardLayout,
  cardLinkColor,
  cardLocation,
  cardBio,
  cardPronoun,
  formattedPronoun,
  visibleContactLinks,
  previewContactLink,
  handleSaveContact
}) => {
  const theme = useTheme();
  const cardThemeValues = theme.card;

  const tracking = useEventTracking();

  const lightenedCardTheme = cardTheme ? lighten(cardTheme, 0.7) : '#fff';
  const handleLinkClick = (link, linkType) => {
    if (tracking && link?.details) {
      const trackingData = {
        linkType: linkType,
        linkUrl: link.details.linkUrl,
        linkTitle: link.details.linkTitle || linkType,
        cardId: cardId,
        cardHolderName: cardFullName,
        timestamp: new Date().toISOString()
      };

      tracking.trackLinkTap(trackingData);

      if (link.details.formattedLink) {
        window.open(link.details.formattedLink, '_blank');
      }
    }
  };

  const allLinks = React.useMemo(() => {
    if (!previewContactLink) return visibleContactLinks || [];
    const linkExists = (visibleContactLinks || []).some(
      link => link.type === previewContactLink.type &&
        link.details?.linkUrl === previewContactLink.details?.linkUrl
    );
    return linkExists ? visibleContactLinks : [...(visibleContactLinks || []), previewContactLink];
  }, [visibleContactLinks, previewContactLink]);

  const formatPronoun = (pronoun) => {
    if (!pronoun) return '';

    switch (pronoun) {
      case 'he': return '(he/him)';
      case 'she': return '(she/her)';
      case 'they': return '(they/them)';
      case 'ze': return '(ze/zir)';
      case 'custom': return '(custom)';
      case '': return '';
      default:
        return `(${pronoun})`;
    }
  };

  const { regularLinks, productsServicesLinks } = React.useMemo(() => {
    if (!allLinks) return { regularLinks: [], productsServicesLinks: [] };

    const regular = [];
    const productsServices = [];

    allLinks.forEach(link => {
      if (link.type === 'Products & Services') {
        productsServices.push(link);
      } else {
        regular.push(link);
      }
    });

    return { regularLinks: regular, productsServicesLinks: productsServices };
  }, [allLinks]);

  const renderCenterAlignedLinks = () => {
    if (!regularLinks || regularLinks.length === 0) return null;

    const rows = [];
    for (let i = 0; i < regularLinks.length; i += 3) {
      const rowLinks = regularLinks.slice(i, Math.min(i + 3, regularLinks.length));

      rows.push(
        <Box
          key={i}
          sx={{
            display: 'flex',
            width: '100%',
            justifyContent: 'center',
            marginBottom: cardThemeValues.links.rowMarginBottom
          }}
        >
          {rowLinks.map((link, index) => (
            <Box
              key={`${i}-${index}`}
              sx={{
                width: '33.333%',
                maxWidth: cardThemeValues.links.itemMaxWidth,
                padding: cardThemeValues.links.itemPadding,
                boxSizing: 'border-box',
                display: 'flex',
                justifyContent: 'center'
              }}
            >
              {link && link.details?.linkUrl && (
                <Box
                  onClick={() => handleLinkClick(link, link.type)}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textDecoration: 'none',
                    color: 'inherit',
                    cursor: 'pointer',
                  }}
                >
                  {link.details?.icon ? (
                    <Box
                      sx={{
                        backgroundColor: cardLinkColor || '#000',
                        width: cardThemeValues.links.iconSize,
                        height: cardThemeValues.links.iconSize,
                        borderRadius: cardThemeValues.links.iconBorderRadius,
                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                        overflow: 'hidden',
                        backgroundImage: `url(${link.details.icon})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                      }}
                    />
                  ) : React.cloneElement(getIconForType(link.type), {
                    sx: {
                      backgroundColor: cardLinkColor || '#000',
                      color: '#fff',
                      fontSize: cardThemeValues.links.iconSize,
                      borderRadius: cardThemeValues.links.iconBorderRadius,
                      padding: cardThemeValues.links.iconPadding,
                      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                    },
                  })}
                  <Typography
                    variant="body2"
                    sx={{
                      textAlign: 'center',
                      marginTop: cardThemeValues.links.titleMarginTop,
                      fontSize: cardThemeValues.links.titleFontSize,
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '100%'
                    }}
                  >
                    {link.details.linkTitle || link.type}
                  </Typography>
                </Box>
              )}
            </Box>
          ))}
        </Box>
      );
    }
    return rows;
  };

  const renderLeftOrPortraitLinks = () => {
    if (!regularLinks || regularLinks.length === 0) return null;
    return regularLinks.map((link, index) => (
      link.details?.linkUrl ? (
        <Box
          key={index}
          onClick={() => handleLinkClick(link, link.type)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            marginTop: cardThemeValues.links.itemMargin,
            textDecoration: 'none',
            color: 'inherit',
            marginBottom: cardThemeValues.links.itemMarginBottom,
            cursor: 'pointer',
          }}
        >
          <Box sx={{ marginRight: cardThemeValues.links.iconMarginRight }}>
            {link.details?.icon ? (
              <Box
                sx={{
                  backgroundColor: cardLinkColor || '#000',
                  width: cardThemeValues.links.leftIconSize,
                  height: cardThemeValues.links.leftIconSize,
                  borderRadius: cardThemeValues.links.leftIconBorderRadius,
                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                  overflow: 'hidden',
                  backgroundImage: `url(${link.details.icon})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              />
            ) : React.cloneElement(getIconForType(link.type), {
              sx: {
                backgroundColor: cardLinkColor || '#000',
                color: '#fff',
                fontSize: cardThemeValues.links.leftIconSize,
                borderRadius: cardThemeValues.links.leftIconBorderRadius,
                padding: cardThemeValues.links.iconPadding,
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
              },
            })}
          </Box>
          <Typography
            variant="body2"
            sx={{
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
              fontWeight: 500,
              fontSize: cardThemeValues.links.leftTitleFontSize
            }}
          >
            {link.details.linkTitle || link.type}
          </Typography>
        </Box>
      ) : null
    ));
  };

  const renderProductsServices = () => {
    if (!productsServicesLinks || productsServicesLinks.length === 0) return null;

    return productsServicesLinks.map((link, index) => (
      <Box key={`products-${index}`} sx={{ mb: 3, width: '100%' }}>
        <ProductsServicesPreview
          link={link}
          cardLinkColor={cardLinkColor}
          isFullDisplay={false}
        />
      </Box>
    ));
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        maxWidth: cardThemeValues.container.maxWidth,
        boxSizing: 'border-box',
        padding: cardThemeValues.container.padding,
        margin: 'auto',
        overflowX: 'hidden',
        position: 'relative',
        '@media (max-width: 480px)': {
          padding: '10px',
        }
      }}
    >
      <Box
        sx={{
          borderRadius: '10px',
          border: '1px solid #cfd8dc',
          boxShadow: '0 4px 6.84px rgba(128, 128, 128, 0.35)',
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          backgroundColor: lightenedCardTheme || '#fff',
          paddingBottom: '30px',
          position: 'relative',
          overflowX: 'hidden',
          overflowY: 'auto',
        }}
      >
        <Box
          sx={{
            width: '100%',
            height: contextCardLayout === 'portrait' ? '500px' : '200px',
            backgroundImage: `url(${contextCardLayout === 'portrait' ? cardProfilePictureUrl || avatarPlaceholder : cardCoverPhotoUrl || placeholderImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            borderTopLeftRadius: '10px',
            borderTopRightRadius: '10px',
            marginBottom: contextCardLayout === 'portrait' ? '0px' : '5px',
            position: 'relative',
            display: 'flex',
            justifyContent: contextCardLayout === 'center-aligned' ? 'center' : 'flex-start',
            alignItems: 'flex-end',
            '&::before': contextCardLayout === 'portrait' ? {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '100%',
              background: `linear-gradient(to bottom, transparent 60%, ${lightenedCardTheme} 100%)`,
              zIndex: 1,
              pointerEvents: 'none',
            } : {},
          }}
        >
          <Box sx={{
            position: 'relative',
            width: '100%',
            margin: contextCardLayout === 'center-aligned' ? '0' : '23px',
            display: 'flex',
            justifyContent: contextCardLayout === 'center-aligned' ? 'center' : 'flex-start',
            alignItems: 'flex-end'
          }}>
            {contextCardLayout !== 'portrait' && (
              <Box
                sx={{
                  position: 'relative',
                  display: 'flex',
                  justifyContent: 'center',
                  width: contextCardLayout === 'center-aligned' ? '100%' : 'auto',
                }}
              >
                <Avatar
                  src={cardProfilePictureUrl || avatarPlaceholder}
                  sx={{
                    width: cardThemeValues.profileImage.size,
                    height: cardThemeValues.profileImage.size,
                    backgroundColor: '#ffffff',
                    border: cardThemeValues.profileImage.border,
                    bottom: cardThemeValues.profileImage.bottom,
                    position: 'relative',
                    zIndex: 2,
                    objectFit: 'cover',
                    padding: cardThemeValues.profileImage.padding,
                    marginLeft: cardThemeValues.profileImage.margin,
                    marginTop: cardThemeValues.profileImage.margin,
                    display: 'inline',
                  }}
                />

                {cardCompanyLogoUrl && contextCardLayout === 'center-aligned' && (
                  <Avatar
                    src={cardCompanyLogoUrl}
                    sx={{
                      width: cardThemeValues.companyLogo.size,
                      height: cardThemeValues.companyLogo.size,
                      position: 'absolute',
                      bottom: cardThemeValues.companyLogo.bottom,
                      right: cardThemeValues.companyLogo.rightOffset,
                      backgroundColor: '#ffffff',
                      zIndex: 2,
                      objectFit: 'cover',
                      padding: cardThemeValues.companyLogo.padding,
                      marginLeft: cardThemeValues.companyLogo.margin,
                      marginTop: cardThemeValues.companyLogo.margin,
                      display: 'inline',
                    }}
                  />
                )}
              </Box>
            )}

            {cardCompanyLogoUrl && contextCardLayout === 'portrait' && (
              <Box
                sx={{
                  position: 'absolute',
                  top: cardThemeValues.portraitLogo.top,
                  right: cardThemeValues.portraitLogo.right,
                  width: cardThemeValues.portraitLogo.size,
                  height: cardThemeValues.portraitLogo.size,
                  zIndex: 2,
                  transform: 'translateX(-100%)',
                }}
              >
                <Avatar
                  src={cardCompanyLogoUrl}
                  sx={{
                    width: '100%',
                    height: '100%',
                    backgroundColor: '#ffffff',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  }}
                />
              </Box>
            )}

            {cardCompanyLogoUrl && contextCardLayout === 'left-aligned' && (
              <Avatar
                src={cardCompanyLogoUrl}
                sx={{
                  width: cardThemeValues.companyLogo.size,
                  height: cardThemeValues.companyLogo.size,
                  position: 'absolute',
                  bottom: cardThemeValues.companyLogo.bottom,
                  left: cardThemeValues.companyLogo.leftOffset,
                  backgroundColor: '#ffffff',
                  zIndex: 2,
                  objectFit: 'cover',
                  padding: cardThemeValues.companyLogo.padding,
                  marginLeft: cardThemeValues.companyLogo.margin,
                  marginTop: cardThemeValues.companyLogo.margin,
                  display: 'inline',
                }}
              />
            )}
          </Box>
        </Box>

        <Box
          sx={{
            marginTop: contextCardLayout === 'portrait'
              ? cardThemeValues.content.marginTop.portrait
              : cardThemeValues.content.marginTop.other,
            paddingLeft: contextCardLayout === 'center-aligned'
              ? 0
              : cardThemeValues.content.padding,
            paddingRight: contextCardLayout === 'center-aligned'
              ? 0
              : cardThemeValues.content.padding,
            textAlign: contextCardLayout === 'center-aligned' ? 'center' : 'left',
            position: 'relative',
            zIndex: 4,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: contextCardLayout === 'center-aligned' ? 'center' : 'flex-start',
              flexWrap: 'wrap'
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontSize: cardThemeValues.typography.nameSize,
                fontWeight: 700,
                fontFamily: `-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif`,
                height: cardThemeValues.typography.nameHeight,
                lineHeight: cardThemeValues.typography.nameLineHeight,
                textAlign: contextCardLayout === 'center-aligned' ? 'center' : 'left',
                color: '#000',
                marginBottom: cardThemeValues.typography.nameMarginBottom,
                marginRight: '0',
              }}
            >
              {cardFullName}
            </Typography>
            {/* {cardPronoun && (
              <Typography
                component="span"
                sx={{
                  display: 'inline-block',
                  alignItems: 'center',
                  boxSizing: 'border-box',
                  color: 'rgb(80, 80, 80)',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                  fontSize: cardThemeValues.typography.pronounSize,
                  fontWeight: 400,
                  height: cardThemeValues.typography.pronounHeight,
                  lineHeight: cardThemeValues.typography.pronounLineHeight,
                  marginBlockEnd: 0,
                  marginBlockStart: cardThemeValues.typography.pronounMarginTop,
                  marginBottom: 0,
                  marginInlineEnd: 0,
                  marginInlineStart: 0,
                  marginLeft: 0,
                  marginRight: 0,
                  marginTop: cardThemeValues.typography.pronounMarginTop,
                  maxWidth: '85%',
                  opacity: 0.8,
                  textAlign: 'left',
                  verticalAlign: 'top',
                  wordBreak: 'break-word',
                  WebkitBoxAlign: 'center',
                  WebkitFontSmoothing: 'subpixel-antialiased'
                }}
              >
                {formattedPronoun ? `(${formattedPronoun})` : formatPronoun(cardPronoun)}
              </Typography>
            )} */}
          </Box>

          {contextCardLayout === 'center-aligned' && cardJobTitle && cardCompany && (
            <Typography
              variant="body2"
              sx={{
                fontSize: cardThemeValues.typography.jobTitleSize,
                color: '#000',
                textAlign: 'center',
                marginBottom: '2px',
                fontWeight: 600,
                fontFamily: `-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif`,
                opacity: 0.8,
                lineHeight: cardThemeValues.typography.jobTitleLineHeight,
              }}
            >
              {`${cardJobTitle} at ${cardCompany}`}
            </Typography>
          )}

          {contextCardLayout === 'center-aligned' && cardJobTitle && !cardCompany && (
            <Typography
              variant="body2"
              sx={{
                fontSize: cardThemeValues.typography.jobTitleSize,
                fontFamily: `-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif`,
                opacity: 0.8,
                lineHeight: cardThemeValues.typography.jobTitleLineHeight,
                color: '#000',
                textAlign: 'center',
                marginBottom: '20px',
                fontWeight: 600,
              }}
            >
              {cardJobTitle}
            </Typography>
          )}

          {contextCardLayout === 'center-aligned' && !cardJobTitle && cardCompany && (
            <Typography
              variant="body2"
              sx={{
                textAlign: 'center',
                marginBottom: '2px',
                fontWeight: 600,
                fontSize: cardThemeValues.typography.jobTitleSize,
                color: '#000',
                fontFamily: `-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif`,
                lineHeight: cardThemeValues.typography.jobTitleLineHeight,
                opacity: 0.8,
              }}
            >
              {cardCompany}
            </Typography>
          )}

          {(contextCardLayout === 'left-aligned' || contextCardLayout === 'portrait') && cardJobTitle && (
            <Typography
              variant="body2"
              sx={{
                fontSize: cardThemeValues.typography.jobTitleSize,
                color: '#000',
                textAlign: 'left',
                marginBottom: '2px',
                fontWeight: 600,
                fontFamily: `-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif`,
                opacity: 0.8,
                lineHeight: cardThemeValues.typography.jobTitleLineHeight,
              }}
            >
              {cardJobTitle}
            </Typography>
          )}

          {(contextCardLayout === 'left-aligned' || contextCardLayout === 'portrait') && cardCompany && (
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                textAlign: 'left',
                marginBottom: '2px',
                fontSize: cardThemeValues.typography.jobTitleSize,
                color: '#000',
                fontFamily: `-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif`,
                lineHeight: cardThemeValues.typography.jobTitleLineHeight,
                opacity: 0.8,
              }}
            >
              {cardCompany}
            </Typography>
          )}

          {cardLocation && (
            <Typography
              variant="body2"
              sx={{
                fontSize: cardThemeValues.typography.locationSize,
                color: '#000',
                textAlign: contextCardLayout === 'center-aligned' ? 'center' : 'left',
                marginBottom: '2px',
                fontWeight: 600,
                fontFamily: `-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif`,
                marginTop: '0.25rem',
                lineHeight: cardThemeValues.typography.locationLineHeight,
                opacity: 0.8,
                wordBreak: 'break-word',
                WebkitFontSmoothing: 'subpixel-antialiased',
              }}
            >
              {cardLocation}
            </Typography>
          )}

          {cardBio && (
            <Typography
              variant="body2"
              sx={{
                fontSize: cardThemeValues.typography.bioSize,
                color: 'rgb(0, 0, 0)',
                textAlign: contextCardLayout === 'center-aligned' ? 'center' : 'left',
                marginBottom: cardThemeValues.typography.bioMargin.bottom,
                marginTop: cardThemeValues.typography.bioMargin.top,
                opacity: 0.8,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                lineHeight: cardThemeValues.typography.bioLineHeight,
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                boxSizing: 'border-box',
                WebkitFontSmoothing: 'subpixel-antialiased',
                marginLeft: contextCardLayout === 'center-aligned' ? '0px' : '0px',
                fontWeight: 500,
              }}
            >
              {cardBio}
            </Typography>
          )}
        </Box>

        <Button
          variant="contained"
          onClick={handleSaveContact}
          sx={{
            backgroundColor: cardLinkColor || '#000',
            width: '85%',
            borderRadius: '8px',
            padding: '10px 0',
            color: '#fff',
            textTransform: 'none',
            marginTop: cardThemeValues.button.marginTop,
            fontSize: cardThemeValues.button.fontSize,
            textTransform: 'capitalize',
            boxShadow: '0 0.1875rem 0.0625rem -0.125rem rgba(0,0,0,0.2), 0 0.125rem 0.125rem 0 rgba(0,0,0,0.14), 0 0.0625rem 0.3125rem 0 rgba(0,0,0,0.12)',
            fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
            letterSpacing: cardThemeValues.button.letterSpacing,
            fontWeight: 700,
            alignSelf: 'center',
            borderRadius: '3.5rem',
            padding: cardThemeValues.button.padding,
          }}
        >
          Save Contact
        </Button>

        <Box
          sx={{
            marginTop: cardThemeValues.links.marginTop,
            paddingLeft: contextCardLayout === 'left-aligned' || contextCardLayout === 'portrait'
              ? cardThemeValues.content.padding
              : 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {contextCardLayout === 'center-aligned'
            ? renderCenterAlignedLinks()
            : renderLeftOrPortraitLinks()}

          {renderProductsServices()}
        </Box>
      </Box>
    </Box>
  );
};

export default DefaultCardDesign;